import type { FC, ReactNode } from 'react';
import { Box, Tooltip, Typography } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faBrain } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IpopLetter } from './ipop-letter';
import { openUnknownDialog } from '../slices/unknownSlice';
import { openMoveDialog } from '../slices/moveSlice';
import { openStakeholderDialog } from '../slices/stakeholderSlice';
import { openFactorDialog } from '../slices/factorSlice';
import { openIntellectualPropertyDialog } from '../slices/intellectualPropertySlice';

const blank = { element: {}, isEditabled: true, isDuplicated: false };

const ipChip: ReactNode = (
  <span
    style={{
      width: 24,
      height: 24,
      borderRadius: 6,
      background: '#f3f4f7',
      color: '#5b6072',
      display: 'grid',
      placeItems: 'center',
      flexShrink: 0,
    }}
  >
    <FontAwesomeIcon icon={faBrain as IconProp} style={{ fontSize: 13 }} />
  </span>
);

/**
 * Quick-action pills on the topbar: each opens the corresponding "create"
 * modal (Unknown / Action / Stakeholder / Factor / IP), mirroring the
 * left-side quick-action buttons.
 */
export const TopbarIpopPills: FC = () => {
  const dispatch = useDispatch();

  const pills: { key: string; label: string; node: ReactNode; action: () => void }[] = [
    { key: 'U', label: 'Unknown', node: <IpopLetter letter="U" size={24} />, action: () => dispatch(openUnknownDialog(blank)) },
    { key: 'A', label: 'Action', node: <IpopLetter letter="A" size={24} />, action: () => dispatch(openMoveDialog(blank)) },
    { key: 'S', label: 'Stakeholder', node: <IpopLetter letter="S" size={24} />, action: () => dispatch(openStakeholderDialog(blank)) },
    { key: 'F', label: 'Factor', node: <IpopLetter letter="F" size={24} />, action: () => dispatch(openFactorDialog(blank)) },
    { key: 'IP', label: 'IP', node: ipChip, action: () => dispatch(openIntellectualPropertyDialog(blank)) },
  ];

  return (
    <Box sx={{ display: 'flex', gap: 0.75, p: '4px', borderRadius: 999, bgcolor: 'action.hover' }}>
      {pills.map((pill) => (
        <Tooltip key={pill.key} title={`New ${pill.label}`} arrow>
          <Box
            onClick={pill.action}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.25,
              py: 0.75,
              borderRadius: 999,
              cursor: 'pointer',
              transition: 'background-color .12s',
              '&:hover': { bgcolor: 'background.paper', boxShadow: 1 },
            }}
          >
            {pill.node}
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.secondary', display: { xs: 'none', xl: 'block' } }}>
              {pill.label}
            </Typography>
          </Box>
        </Tooltip>
      ))}
    </Box>
  );
};
